// ./src/utils/api/FormFieldIndexService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Form } from "./FormService";
import { FormField } from "./FormFieldService";

// GraphQL queries
const FORM_FIELD_INDEX_LIST_QUERY = gql`
  query FormFieldIndexList(
    $filter: FormFieldIndexFilter
    $params: PaginationAndSorting
  ) {
    formFieldIndexList(filter: $filter, params: $params) {
      code
      data {
        id
        accountId
        formId
        formFieldId
        fieldValue
        fieldValueQty
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_FIELD_INDEX_GET_QUERY = gql`
  query FormFieldIndexGet($id: ID) {
    formFieldIndexGet(id: $id) {
      code
      data {
        id
        accountId
        formId
        formFieldId
        fieldValue
        fieldValueQty
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const FORM_FIELD_INDEX_GET_MANY_QUERY = gql`
  query FormFieldIndexGetMany($ids: [ID]) {
    formFieldIndexGetMany(ids: $ids) {
      code
      data {
        id
        accountId
        formId
        formFieldId
        fieldValue
        fieldValueQty
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface FormFieldIndex {
  id?: string | undefined;
  accountId?: string | undefined;
  formId?: string | undefined;
  formFieldId?: string | undefined;
  fieldValue?: string | undefined;
  fieldValueQty?: number | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  form?: Form;
  formField?: FormField;
}

export interface FormFieldIndexFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  formId?: string[] | null | undefined;
  formFieldId?: string[] | null | undefined;
  fieldValue?: string[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

/**
 * FormFieldIndex service that handles form field index-related API calls
 * Read-only operations: list, get, getMany (indexes are managed internally by the system)
 */
export class FormFieldIndexService {
  /**
   * Get a list of form field indexes with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: FormFieldIndexFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(FORM_FIELD_INDEX_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single form field index by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(FORM_FIELD_INDEX_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple form field indexes by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      FORM_FIELD_INDEX_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }
}
